import {
  Button,
  Card,
  CardBody,
  Code,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Form } from "react-router-dom";
import { useStoreApi } from "reactflow";
import { useQuestProvider } from "../../context/QuestContext";
import { useNodeTypesProvider } from "../../context/reactflow/NodeTypesProvider";
import useExportRuntimeData from "../../hooks/useExportRuntimeData";
import { Deploy, deployVersions } from "../../models/api/deploy";
import { NodeData } from "../../models/nodeType";
import FormInput from "../../ui/form/FormInput";
import FormRadioWithDescription from "../../ui/form/FormRadioWithDescription";
import getRewards from "../../utils/quest/getRewards";

function QuestDeployVersionButton() {
  const { isOpen, onToggle, onClose } = useDisclosure();

  const { quest, saveQuest, isUpdating } = useQuestProvider();

  const { register, watch, handleSubmit, reset } = useForm<Deploy>({
    defaultValues: useMemo(
      () => ({
        deployVersion: quest?.deployVersion,
        deployVersionType: "minor",
        deployMessage: quest?.deployMessage,
      }),
      [quest]
    ),
    mode: "onSubmit",
  });

  useEffect(
    () =>
      reset({
        deployVersion: quest?.deployVersion,
        deployVersionType: "minor",
        deployMessage: quest?.deployMessage,
      }),
    [quest]
  );

  const store = useStoreApi();

  const { nodeTypes } = useNodeTypesProvider();

  const currentDeployVersion = watch("deployVersion", "0.0.0");
  const currentDeployVersionType = watch("deployVersionType");

  const updatedDeployVersion = useMemo(() => {
    if (currentDeployVersion == null) {
      return;
    }

    const [major, minor, patch] = currentDeployVersion.split(".");

    let updatedMajor = Number(major);
    let updatedMinor = Number(minor);
    let updatedPatch = Number(patch);

    if (currentDeployVersionType === "major") {
      updatedMajor += 1;
      updatedMinor = 0;
      updatedPatch = 0;
    }

    if (currentDeployVersionType === "minor") {
      updatedMinor += 1;
      updatedPatch = 0;
    }

    if (currentDeployVersionType === "patch") {
      updatedPatch += 1;
    }

    return `${updatedMajor}.${updatedMinor}.${updatedPatch}`;
  }, [currentDeployVersion, currentDeployVersionType]);

  const { exportQuestNodes } = useExportRuntimeData();

  const toast = useToast();

  const handleUpdate = useCallback(
    ({ deployMessage }: Deploy) => {
      if (quest == null) {
        return;
      }

      const qaStatus = quest.qaStatus ?? {
        issueId: "",
        status: "Not Tested",
      };

      if (currentDeployVersionType === "major") {
        qaStatus.status = "Not Tested";
      }

      const nodes = store.getState().getNodes();
      const edges = store.getState().edges;

      const updatedNodes = nodes.map((node) => {
        const initialNodeType = nodeTypes.find(({ nodeName }) => nodeName === node.data.nodeName);
        const jsonSchema = initialNodeType?.nodeData?.templateData?.jsonSchema;

        const {
          data: { nodeData = {} },
        } = node;

        const updatedNodeData: NodeData = {
          ...nodeData,
          templateData: {
            ...nodeData.templateData,
            jsonSchema,
          },
        };

        return {
          ...node,
          data: {
            ...node.data,
            nodeData: updatedNodeData,
          },
        };
      });

      const updatedEdges = edges.slice();

      const rewards = getRewards(updatedNodes, updatedEdges);

      saveQuest(
        quest.questId,
        { nodes: updatedNodes, edges: updatedEdges },
        {
          deployVersion: updatedDeployVersion,
          deployMessage,
          qaStatus,
          rewards,
        },
        () => {
          reset({ deployVersion: updatedDeployVersion, deployVersionType: "minor", deployMessage });

          onClose();
        }
      );
    },
    [
      saveQuest,
      exportQuestNodes,
      reset,
      onClose,
      toast,
      quest,
      store,
      nodeTypes,
      updatedDeployVersion,
      currentDeployVersionType,
    ]
  );

  if (quest == null) {
    return null;
  }

  return (
    <Popover placement={"left-end"} isOpen={isOpen} onClose={onClose} returnFocusOnClose={false}>
      <PopoverTrigger>
        <Button color={"green.600"} onClick={onToggle}>
          <Text color={"green.600"} casing={"uppercase"}>
            Save
          </Text>
        </Button>
      </PopoverTrigger>

      <Portal>
        <PopoverContent bg={"theme.dark.background"} borderColor={"green.600"} borderWidth={2}>
          <Form onSubmit={handleSubmit(handleUpdate)}>
            <PopoverBody>
              <Stack>
                <Card bg={"theme.dark.background"}>
                  <CardBody>
                    <Stack>
                      <FormRadioWithDescription
                        {...register("deployVersionType")}
                        values={deployVersions}
                        value={currentDeployVersionType}
                      >
                        <Text color={"white"} casing={"uppercase"} fontWeight={400}>
                          Version <Code>{updatedDeployVersion}</Code>
                        </Text>
                      </FormRadioWithDescription>

                      <FormInput color={"white"} {...register("deployMessage")} borderWidth={1}>
                        <Text color={"white"} casing={"uppercase"} fontWeight={400}>
                          Description
                        </Text>
                      </FormInput>
                    </Stack>
                  </CardBody>
                </Card>

                <Button color={"white"} type={"submit"} isLoading={isUpdating}>
                  <Text color={"white"} casing={"uppercase"}>
                    Save
                  </Text>
                </Button>
              </Stack>
            </PopoverBody>
          </Form>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

export default memo(QuestDeployVersionButton);
